/**
 * Interview Candidates always OPENS on the shortlist. Never on the whole table.
 *
 * Out here rather than inside the page because the default is the whole
 * behaviour, and a default with no test is one that drifts. This one drifted
 * twice.
 *
 * First it read `=== 'true'`, so an absent key meant off — and an absent key
 * is every new user, every new machine and every cleared cache. A screen
 * called Interview Candidates opened on call backs, not-interesteds and
 * location issues.
 *
 * Flipping that to `!== 'false'` fixed the absent key and nothing else. An
 * explicit `'false'` was still honoured, on the reasonable-sounding ground
 * that unticking a box is a choice worth remembering. It is not, here: the
 * box gets unticked once — to find somebody who arrived by import — and from
 * then on that browser opens on 47,000 rows forever, which is the original
 * complaint wearing a different hat. It was reported a second time from a
 * browser that had the key set, and the code looked correct both times.
 *
 * So nothing is persisted now. The checkbox still reaches the whole table,
 * because one case genuinely needs it — an import of two thousand people has
 * no calls yet, so nobody in it has a latest call to be positive — but that
 * widening lasts for the session and dies with it. Somebody who wants the
 * uncalled as a worklist has the Daily Calls Report's still-to-call list,
 * which is built for exactly them.
 *
 * What the filter means is decided on the server: `latest_call_positive`
 * keeps candidates whose *most recent* call was Interested or Shortlisted.
 * Most recent, not ever — somebody interested in June and unreachable last
 * week is not an interested candidate, and a list built on "has ever" only
 * grows.
 */

/**
 * What the screen opens on, every time, for everybody.
 *
 * A constant rather than a read of anything. That is the point: there is no
 * per-browser state left that can answer this question differently, so the
 * screen cannot be wrong about it on one machine and right on another.
 */
export const INTERESTED_ONLY_DEFAULT = true;

/**
 * The key the old builds wrote to, kept only so it can be cleared.
 *
 * Browsers that unticked the box before this change still carry `'false'`
 * here. Nothing reads it any more, so it is inert — but leaving a key named
 * `interestedOnly` lying around for a future reader to wire back up is how
 * this regresses a third time. {@link forgetInterestedOnly} removes it.
 */
export const INTERESTED_ONLY_KEY = 'candidates.interestedOnly';

/** Drop the stale preference. Safe to call when it was never written. */
export function forgetInterestedOnly(): void {
  try {
    window.localStorage.removeItem(INTERESTED_ONLY_KEY);
  } catch {
    // Private browsing, or storage disabled. There is nothing to clean up
    // that could affect the screen either way — the default is a constant.
  }
}
